import React from "react";
import { Modal, Button, Text, Flex, toast } from "@jsq/ds/core";
import {
  Form,
  FormRadioGroup as RadioGroup,
  FormRadioButton as RadioButton,
  useFormContext,
} from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import {
  useUpdateDashboardShareMutation,
  DashboardShareType,
} from "../../graphql/supergraph.index";

interface SharingModalProps {
  showModal: boolean;
  dashboardId: string | number;
  arenaId: string | number;
  shareType?: ShareType;
  onHide: () => void;
  onShareCompleted?: () => void;
}

export enum ShareType {
  PRIVATE = "private",
  PUBLIC = "public",
  INDIVIDUAL = "individual",
}

interface SharingFormValues {
  shareType?: ShareType;
}

// Helper function to convert ShareType enum to DashboardShareType enum
const shareTypeToDashboardShareType = (shareType?: ShareType): DashboardShareType => {
  switch (shareType) {
    case ShareType.PUBLIC:
      return "PUBLIC" as DashboardShareType;
    case ShareType.INDIVIDUAL:
      return "INDIVIDUAL" as DashboardShareType;
    case ShareType.PRIVATE:
    default:
      return "PRIVATE" as DashboardShareType;
  }
};

const SharingFormContent: React.FC<{ loading: boolean }> = ({ loading }) => {
  const { isSubmitting } = useFormContext<SharingFormValues>();

  return (
    <>
      <Modal.Body>
        <RadioGroup name="shareType">
          <RadioButton value={ShareType.PRIVATE}>
            <Flex flexDirection="column" gap={4}>
              <Text>Not shared</Text>
              <Text variant="secondary-sm" color={Colors.Neutral600}>
                Only you have access to this dashboard
              </Text>
            </Flex>
          </RadioButton>
          <RadioButton value={ShareType.PUBLIC}>
            <Flex flexDirection="column" gap={4}>
              <Text>Shared with all</Text>
              <Text variant="secondary-sm" color={Colors.Neutral600}>
                Anyone in your workspace can view this dashboard if they have access to
                underlying data
              </Text>
            </Flex>
          </RadioButton>
        </RadioGroup>
      </Modal.Body>
      <Modal.Footer>
        <Flex justifyContent="flex-end">
          <Button type="submit" variant="primary" disabled={isSubmitting || loading}>
            Save
          </Button>
        </Flex>
      </Modal.Footer>
    </>
  );
};

export const SharingModal: React.FC<SharingModalProps> = ({
  showModal,
  dashboardId,
  arenaId,
  shareType,
  onHide,
  onShareCompleted,
}) => {
  const [updateDashboardShare, { loading }] = useUpdateDashboardShareMutation({
    onError: () => {
      toast.error(`Unable to update sharing settings`);
    },
    onCompleted: () => {
      toast.success("Sharing settings updated successfully");
      onHide();
      onShareCompleted?.();
    },
  });

  const handleSubmit = (values: SharingFormValues) => {
    // Fallback to the original shareType prop if a form value hasn't been selected
    const effectiveShareType = values.shareType ?? shareType;
    const newShareType = shareTypeToDashboardShareType(effectiveShareType);

    const parsedDashboardId =
      typeof dashboardId === "string" ? parseInt(dashboardId, 10) : dashboardId;
    const parsedArenaId = typeof arenaId === "string" ? parseInt(arenaId, 10) : arenaId;

    void updateDashboardShare({
      variables: {
        input: {
          id: parsedDashboardId,
          arenaId: parsedArenaId,
          shareType: newShareType,
        },
      },
    });
  };

  return (
    <Modal size="small" show={showModal} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Sharing</Modal.Title>
      </Modal.Header>
      <Form initialValues={{ shareType }} onSubmit={handleSubmit}>
        <SharingFormContent loading={loading} />
      </Form>
    </Modal>
  );
};

export default SharingModal;
